<!-- eslint-disable vue/multi-word-component-names -->
<script setup lang="ts">
import {
    computed,
    type FunctionalComponent,
    h,
    mergeProps,
    useSlots,
} from 'vue';
import { fieldPropDefs } from './props';
import { useFieldErrors } from './errors';

import Skeleton from 'primevue/skeleton';
import InputText from 'primevue/inputtext';

const props = defineProps({
    ...fieldPropDefs,
    noReadonlyTextInputFallback: {
        type: Boolean,
        default: false,
    },
});

const emit = defineEmits<{
    (e: 'labelClick', event: MouseEvent): void;
}>();

const slots = useSlots();

// HELPER MESSAGE

const { hasErrors, errorMessage } = useFieldErrors(() => props.errors);

// LAYOUT

const isLoading = computed(() => !!props.loading);
const isRequired = computed(() => !!props.required);
const isInvalid = computed(() => !!props.invalid || hasErrors.value);
const isValid = computed(() => props.valid === true && !isInvalid.value);

const showLabel = computed(() => !!props.label || !!slots.label);

const layout = computed(() => {
    const layout = props.layout ?? 'vertical';
    if (layout === 'float' && !showLabel.value) return 'vertical';
    return layout;
});

// PROPS
const fieldClass = computed(() => [
    'field',
    `field-layout-${layout.value}`,
    {
        grid: layout.value === 'horizontal',
        invalid: isInvalid.value,
        valid: isValid.value,
        required: isRequired.value,
    },
    props.class,
]);

// Helper components
const FHelpText = () =>
    !!errorMessage.value || !!props.helper || !!slots.helper
        ? h(
              'small',
              {
                  class: ['field-helper'],
              },
              errorMessage.value ??
                  (slots.helper
                      ? slots.helper({ helper: props.helper })
                      : props.helper)
          )
        : undefined;

const FRequiredStar = () =>
    props.required
        ? h('span', { class: ['label-required-star'] }, '*')
        : undefined;

const FLabel = (labelProps: Record<string, unknown>) => {
    return showLabel.value
        ? h(
              'label',
              mergeProps(
                  {
                      for: props.for,
                      class: ['field-label'],
                      onClick: (event: MouseEvent) => emit('labelClick', event),
                  },
                  labelProps
              ),
              (slots.label ? slots.label() : undefined) ??
                  h('span', {}, [props.label, FRequiredStar()])
          )
        : undefined;
};

const FSkeleton: FunctionalComponent = () => {
    return (
        (slots.skeleton ? slots.skeleton() : undefined) ??
        h(Skeleton, {
            height: '2rem',
            width: '100%',
        })
    );
};

const FInput = () => {
    const binds = {
        invalid: isInvalid.value,
        layout: layout.value,
    };
    return slots.default ? slots.default(binds) : h('pre', {}, binds);
};

const FReadonly: FunctionalComponent = () => {
    const displayValue =
        typeof props.modelValue === 'string'
            ? props.modelValue.toString()
            : '-- geen waarde --';

    return (
        (slots.readonly ? slots.readonly() : undefined) ??
        (props.noReadonlyTextInputFallback
            ? FInput()
            : h(InputText, {
                  readonly: true,
                  disabled: true,
                  modelValue: displayValue,
              }))
    );
};
</script>

<template>
    <div :class="fieldClass" :style="props.style">
        <template v-if="layout === 'float'">
            <div v-if="isLoading">
                <FSkeleton />
            </div>
            <div v-else class="p-float-label">
                <template v-if="readonly === false">
                    <FInput />
                </template>
                <template v-else>
                    <FReadonly />
                </template>
                <FLabel />
            </div>
            <FHelpText />
        </template>
        <template v-else>
            <FLabel
                :class="{
                    'col-12 mb-2 md:col-4 md:mb-0': layout === 'horizontal',
                }"
            />
            <div
                key="fieldContainer"
                class="field-wrapper p-fluid"
                :class="{ 'col-12 md:col-8': layout === 'horizontal' }"
            >
                <FSkeleton v-if="isLoading" />
                <template v-else>
                    <template v-if="readonly === false">
                        <FInput />
                    </template>
                    <template v-else>
                        <FReadonly />
                    </template>
                </template>
                <FHelpText />
            </div>
        </template>
    </div>
</template>
